import { captureException } from '@sentry/react';
import { DateTime } from 'luxon';
import { types } from 'mobx-state-tree';

import { autoFlow } from '@/stores/storeUtils';
import { authorizedApi } from '@/utils/apiUtils';

export const AppSumoTiers = {
  1: 'catchappbookings_tier1',
  2: 'catchappbookings_tier2',
  3: 'catchappbookings_tier3',
  4: 'catchappbookings_tier4',
  5: 'catchappbookings_tier5',
};

const Coupon = types.model({
  duration: types.maybeNull(types.string),
  percent_off: types.maybeNull(types.number),
});

const Discount = types.model({
  coupon: types.maybeNull(Coupon),
});

const Price = types.model({
  id: types.maybeNull(types.string),
  unit_amount: types.maybeNull(types.number),
  currency: types.maybeNull(types.string),
  interval: types.maybeNull(types.string),
  interval_count: types.maybeNull(types.number),
});

export const Subscription = types
  .model({
    created: types.maybeNull(types.integer),
    current_period_end: types.maybeNull(types.integer),
    discount: types.maybeNull(Discount),
    id: types.string,
    is_trial: types.boolean,
    appsumo_plan: types.maybeNull(types.string),
    price: types.maybeNull(Price),
    status: types.maybeNull(types.string),
    quantity: types.maybeNull(types.number),
  })
  .views((self) => ({
    get isTrial() {
      return self.is_trial;
    },

    get isTrialing() {
      return self.status === 'trialing' || self.is_trial;
    },
  }));

export const defaultSubscriptionInterval = 'month';

export const normalizeSubscription = (data) => ({
  id: data.id,
  created: DateTime.fromSeconds(data.created).toMillis(),
  current_period_end: data.current_period_end
    ? DateTime.fromSeconds(data.current_period_end).toMillis()
    : null,
  is_trial: Boolean(data.is_trial),
  appsumo_plan: data.appsumo_plan ?? null,
  status: data.status ?? null,
  quantity: data.quantity ?? null,
  discount: data.discount?.coupon
    ? {
        coupon: {
          duration: data.discount.coupon.duration ?? null,
          percent_off: data.discount.coupon.percent_off ?? null,
        },
      }
    : null,
  price: data.price
    ? {
        id: data.price.id ?? null,
        unit_amount: data.price.unit_amount ?? null,
        currency: data.price.currency ?? null,
        interval: data.price.interval ?? null,
        interval_count: data.price.interval_count ?? null,
      }
    : null,
});

const SubscriptionStore = types
  .model('SubscriptionStore', {
    subscription: Subscription,
  })
  .actions((self) =>
    autoFlow({
      *getSubscription() {
        const userId = authorizedApi.defaults.headers.id;

        try {
          const response = yield authorizedApi.get(
            `/appointments/users/${userId}/subscription`,
          );

          const data = response.data?.subscription;

          self.subscription = normalizeSubscription(data);
        } catch (error) {
          self.subscription = {
            id: 'no-subscription-id',
            created: 0,
            current_period_end: 0,
            is_trial: true,
          };

          if (!error?.response) {
            captureException(error);
          }
        }
      },
      *cancelSubscription(reason) {
        const userId = authorizedApi.defaults.headers.id;

        yield authorizedApi.post(
          `/appointments/users/${userId}/cancel-subscription`,
          { cancellationReason: reason },
        );
      },
    }),
  );

const subscriptionStore = SubscriptionStore.create({
  subscription: {
    id: '',
    is_trial: false,
  },
});

export default subscriptionStore;
